import { stringify } from 'qs';
import { ReviveDateTime } from './JSON';

export interface IParams {
    [key: string]: any;
}

export interface IConfig {
    method?: string;
    data?: string | {};
    headers?: Headers;
    params?: IParams;
    signal?: RequestInit['signal'];
    cache?: boolean;
    timeout?: number;
}
interface IHeaderConfigObject {
    headers: Headers;
    accessToken?: string;
}

export interface IAccessTokenStore {
    getAccessToken(): Promise<string | undefined>;
}

export class HttpClient {
    private static defaultConfig: IConfig = {
        headers: new Headers(),
    };

    public async get<T>(url: string, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config, 'get');

        const requestInit: RequestInit = {
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'GET',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public async post<T>(url: string, data?: any, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config);

        const requestInit: RequestInit = {
            body: data ? JSON.stringify(data) : null,
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'POST',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public async put<T>(url: string, data?: any, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config);

        const requestInit: RequestInit = {
            body: data ? JSON.stringify(data) : null,
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'PUT',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public async delete<T>(url: string, config: IConfig = HttpClient.defaultConfig): Promise<T | undefined> {
        const { headers } = await this.setHeaders(config);

        const requestInit: RequestInit = {
            cache: 'default',
            credentials: 'same-origin',
            headers,
            method: 'DELETE',
            mode: 'cors',
            signal: config.signal,
        };

        url = url + this.parseParams(config.params);

        return this.exec<T>(url, requestInit, config);
    }

    public setAccessTokenStore(accessTokenStore: IAccessTokenStore) {
        this.accessTokenStore = accessTokenStore;
    }

    public setCsrfToken(csrfToken: string | undefined) {
        this.csrfToken = csrfToken;
    }

    private async setHeaders(
        config: IConfig = HttpClient.defaultConfig,
        method?: string
    ): Promise<IHeaderConfigObject> {
        const headers = config.headers ? new Headers(config.headers) : new Headers();
        const accessToken = this.accessTokenStore && (await this.accessTokenStore.getAccessToken());
        if (accessToken) headers.append('Authorization', 'Bearer ' + accessToken);
        if (this.csrfToken) headers.append('X-CSRF-TOKEN', this.csrfToken);
        if (method?.toLowerCase() !== 'get') headers.append('Content-Type', 'application/json');
        return { headers, accessToken };
    }

    private parseParams(params?: IParams) {
        if (params) {
            const query = stringify(params);
            if (query.length) {
                return '?' + query;
            }
        }
        return '';
    }

    private exec<T>(
        url: string,
        requestInit: RequestInit,
        config: IConfig,
        repeat: boolean = false
    ): Promise<T | undefined> {
        let promise: Promise<T | undefined>;

        if (config.cache) {
            promise = this.cache.get<T>(url, requestInit);

            if (promise) return promise;
        }

        promise = this.fetchAsync<T>(url, requestInit, config, repeat);

        if (config.cache) {
            this.cache.set(url, requestInit, promise);
        }

        return promise;
    }

    private async fetchAsync<T>(
        url: string,
        requestInit: RequestInit,
        config: IConfig,
        repeat: boolean = false
    ): Promise<T | undefined> {
        const { timeout } = config;
        const fetchConfig = { ...requestInit };
        const abortSignal = AbortSignal as any;
        if (timeout) fetchConfig.signal = abortSignal.timeout(timeout);
        let response: Response;
        try {
            response = await fetch(url, fetchConfig);
        } catch (e: any) {
            // #191: mark failures of the request itself (no connection, DNS, CORS, reset) so callers can
            // tell a dropped connection from a bug - a TypeError thrown by THEIR code after a successful
            // response must not look like "server unreachable". Aborts keep their AbortError as-is.
            if (e && typeof e === 'object' && e.name !== 'AbortError') e.isNetworkError = true;
            throw e;
        }
        return this.parseOrThrowAsync<T>(response, { requestInit, url, config }, repeat);
    }

    private async parseOrThrowAsync<T>(
        response: Response,
        requestConfig: { url: string; requestInit: RequestInit; config?: any },
        repeat: boolean = false
    ): Promise<T | undefined> {
        const { url, requestInit, config } = requestConfig;
        const contentType = response.headers.get('Content-Type') || '';
        const { status, statusText } = response;

        // 401 → retry once, then RETURN the result
        if (status === 401 && !repeat) {
            const { headers } = await this.setHeaders(config, requestInit.method);
            requestInit.headers = headers;
            return await this.exec<T>(url, requestInit, config, true);
        }

        // Read body text once
        const text = await response.text();
        const baseErr = { status, statusText, url };

        // JSON path (also RFC7807 `application/problem+json`, which our API returns on 5xx)
        if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
            let data: any = undefined;
            try {
                data = text ? JSON.parse(text, ReviveDateTime) : undefined;
            } catch {
                if (!response.ok) {
                    throw { ...baseErr, message: 'Invalid JSON in error response', body: text };
                }
                // ok=true but malformed JSON (unexpected) → no value
                return undefined;
            }

            if (response.ok) {
                // Successful JSON response
                return data as T;
            } else {
                // Error JSON response
                if (status === 429) {
                    const retryAfter = response.headers.get('retry-after') ?? undefined;
                    throw {
                        ...baseErr,
                        retryAfter,
                        body: data,
                        message: `Too many requests.${retryAfter ? ` Try again after ${retryAfter} seconds.` : ''}`,
                    };
                }
                // Message precedence, covering every error shape this API can return:
                //   1. `message` - the live WebApiExceptionFilterAttribute payload
                //                  ({ exceptionType, message }) and the #92 400 guard-rail.
                //   2. `detail`  - RFC7807 from ApiExceptionFilter (#30): says *why* it failed,
                //                  e.g. "the display service did not respond in time".
                //   3. `title`   - RFC7807 fallback when there is no detail.
                // An explicit `message` wins because it is the only field that ever carries
                // server-authored, user-facing guidance. traceId is carried through when present
                // so the UI can quote it to support.
                const serverMessage =
                    data && typeof data === 'object' ? (data as { message?: string }).message : undefined;
                throw {
                    ...baseErr,
                    body: data,
                    traceId: data?.traceId,
                    // #191: a 503 may say how long to back off (the SQL-timeout response does); callers
                    // that retry honour it. Absent unless the server sent it.
                    retryAfter: response.headers.get('retry-after') ?? undefined,
                    message: serverMessage || data?.detail || data?.title || statusText || `HTTP ${status}`,
                };
            }
        }

        // Non-JSON path
        if (response.ok) {
            // Empty body success (e.g., 204)
            if (!text) return undefined;
            // If you prefer to allow text bodies on success, return text as unknown as T
            throw new Error('Not a valid json response ' + text);
        } else {
            if (status === 429) {
                const retryAfter = response.headers.get('retry-after') ?? undefined;
                throw {
                    ...baseErr,
                    retryAfter,
                    message: `Too many requests.${retryAfter ? ` Try again after ${retryAfter} seconds.` : ''}`,
                };
            }
            throw { ...baseErr, message: statusText || `HTTP ${status}`, body: text || undefined };
        }
    }

    private accessTokenStore?: IAccessTokenStore;
    private csrfToken?: string;
    private cache = new Cache();
}

class Cache {
    public get<T>(url: string, requestInit: RequestInit) {
        const key = this.genKey(url, requestInit);
        return this.cache.get(key) as Promise<T | undefined>;
    }

    public set<T>(url: string, requestInit: RequestInit, response: Promise<T | undefined>) {
        const key = this.genKey(url, requestInit);
        this.cache.set(key, response);
    }

    public delete(url: string, requestInit: RequestInit) {
        const key = this.genKey(url, requestInit);
        this.cache.delete(key);
    }

    private genKey(url: string, requestInit: RequestInit) {
        return JSON.stringify({
            url,
            requestInit,
        });
    }

    private cache = new Map<string, Promise<any>>();
}
